import { BreadcrumbLabel } from "@/types";
import { CalendarTodayOutlined, EventNote, SvgIconComponent, FolderOutlined, MergeType, CalendarMonth, ListAlt, EventRepeatOutlined, PersonOutlined, AdminPanelSettingsOutlined, TableChartOutlined, SettingsSuggestOutlined, LowPriority, AssessmentOutlined, BadgeOutlined } from "@mui/icons-material";

export interface NavItem {
    text: string;
    route: string;
    defaultParams?: Record<string, any>; // default params for the route
    icon: SvgIconComponent;
    breadcrumbLabel?: BreadcrumbLabel;
    desktopNavOnly?: boolean;
    permission: string;
}

// Define metadata for each route
export const routeMetadata: Record<string, NavItem> = {
    // User management
    'users.index': {
        text: 'Users',
        route: 'users.index',
        icon: PersonOutlined,
        breadcrumbLabel: 'Users',
        permission: 'users',
    },
    'users.show': {
        text: 'Users',
        route: 'users.show',
        icon: PersonOutlined,
        breadcrumbLabel: 'User',
        permission: 'users',
    },
    'users.show.changelog': {
        text: 'User Change Log',
        route: 'users.show.changelog',
        icon: EventNote,
        breadcrumbLabel: 'Change Log',
        permission: 'users',
    },
    'users.sso-roles.index': {
        text: 'SSO roles',
        route: 'users.sso-roles.index',
        icon: AdminPanelSettingsOutlined,
        breadcrumbLabel: 'SSO roles',
        permission: 'users',
    },
    'users.external.index': {
        text: 'External Users',
        route: 'users.external.index',
        icon: BadgeOutlined,
        breadcrumbLabel: 'External Users',
        permission: 'external_users',
    },
    'users.external.create': {
        text: 'External Users',
        route: 'users.external.create',
        icon: BadgeOutlined,
        breadcrumbLabel: 'Create External User',
        permission: 'external_users',
    },
    // CWC events
    'events.index': {
        text: 'Events',
        route: 'events.index',
        icon: ListAlt,
        breadcrumbLabel: 'All events',
        permission: 'cwc_events',
    },
    'events.calendar.index': {
        text: 'Events Calendar',
        route: 'events.calendar.index',
        defaultParams: {
            'event_status': ['Confirmed'],
        },
        icon: CalendarMonth,
        breadcrumbLabel: 'Calendar',
        permission: 'cwc_events',
    },
    'events.show': {
        text: 'Events',
        route: 'events.show',
        icon: CalendarTodayOutlined,
        breadcrumbLabel: 'Event',
        permission: 'cwc_events',
    },
    'events.show.changelog': {
        text: 'Event Change Log',
        route: 'events.show.changelog',
        icon: EventNote,
        breadcrumbLabel: 'Change Log',
        permission: 'cwc_events',
    },
    'events.show.occurrences': {
        text: 'Event Occurrences',
        route: 'events.show.occurrences',
        icon: EventRepeatOutlined,
        breadcrumbLabel: 'Occurrences',
        permission: 'cwc_events',
    },
    'events.show.conflicts': {
        text: 'Event Conflicts',
        route: 'events.show.conflicts',
        icon: MergeType,
        breadcrumbLabel: 'Resolve Conflicts',
        permission: 'cwc_events',
    },
    'event-groups.show.events': {
        text: 'Group Details',
        route: 'event-groups.show.events',
        icon: FolderOutlined,
        breadcrumbLabel: 'Group',
        permission: 'cwc_events',
    },
    'event-groups.index': {
        text: 'Groups',
        route: 'event-groups.index',
        icon: FolderOutlined,
        breadcrumbLabel: 'Groups',
        permission: 'cwc_events',
    },
    // IEBMS Events
    'iebms-events.index': {
        text: 'Taukaea Events',
        route: 'iebms-events.index',
        icon: ListAlt,
        breadcrumbLabel: 'All Taukaea Events',
        permission: 'iebms_events',
    },
    'iebms-events.bookings-calendar': {
        text: 'Taukaea Calendar',
        route: 'iebms-events.bookings-calendar',
        icon: CalendarMonth,
        breadcrumbLabel: 'Bookings Calendar',
        permission: 'iebms_events_calendar',
    },
    // IEBMS Post Event reporting templates
    'iebms-template.index': {
        text: 'Report Templates',
        route: 'iebms-template.index',
        icon: TableChartOutlined,
        breadcrumbLabel: 'Report Templates',
        permission: 'iebms_report_template',
    },
    'iebms-template.create': {
        text: 'Report Templates',
        route: 'iebms-template.create',
        icon: TableChartOutlined,
        breadcrumbLabel: 'Create Report Template',
        permission: 'iebms_report_template',
    },
    // iEBMS post event reports
    'iebms-reports.show.changelog': {
        text: 'Report Change Log',
        route: 'iebms-reports.show.changelog',
        icon: EventNote,
        breadcrumbLabel: 'Change Log',
        permission: 'iebms_report',
    },
    'sh-events.index': {
        text: 'Strategic Invites',
        route: 'sh-events.index',
        icon: ListAlt,
        breadcrumbLabel: 'All Strategic Invites',
        permission: 'sh_events',
    },
    'sh-events.show': {
        text: 'Strategic Invite',
        route: 'sh-events.show',
        icon: CalendarTodayOutlined,
        breadcrumbLabel: 'Event',
        permission: 'sh_events',
    },
    'sh-events.show.changelog': {
        text: 'Strategic Invite Change Log',
        route: 'sh-events.show.changelog',
        icon: EventNote,
        breadcrumbLabel: 'Change Log',
        permission: 'sh_events',
    },
    'auto-actions.index': {
        text: 'Auto Actions',
        route: 'auto-actions.index',
        icon: SettingsSuggestOutlined,
        breadcrumbLabel: 'Auto Actions',
        permission: 'auto_actions',
    },
    'auto-actions.config': {
        text: 'Auto Action Config',
        route: 'auto-actions.config',
        icon: SettingsSuggestOutlined,
        breadcrumbLabel: 'Configuration',
        permission: 'auto_actions',
    },
    'auto-actions.history': {
        text: 'Auto Action History',
        route: 'auto-actions.history',
        icon: SettingsSuggestOutlined,
        breadcrumbLabel: 'History',
        permission: 'auto_actions',
    },
    'auto-actions.changelog': {
        text: 'Auto Action Changelog',
        route: 'auto-actions.changelog',
        icon: SettingsSuggestOutlined,
        breadcrumbLabel: 'Change Log',
        permission: 'auto_actions',
    },
    'auto-actions.reorder.show': {
        text: 'Reorder Auto Actions',
        route: 'auto-actions.reorder.show',
        icon: LowPriority,
        breadcrumbLabel: 'Reorder Auto Actions',
        permission: 'auto_actions',
    },
    'iebms-reports.multi-report': {
        text: 'Multi-Report Page',
        route: 'iebms-reports.multi-report',
        icon: AssessmentOutlined,
        breadcrumbLabel: 'Multi-Report Page',
        permission: 'iebms_report',
    },
}

export const navRoutes = [
    'events.index',
    'events.calendar.index',
    'event-groups.index',
    'auto-actions.index',
    'iebms-events.index',
    'iebms-events.bookings-calendar',
    'iebms-template.index',
    'iebms-reports.multi-report',
    'sh-events.index',
    'users.index',
    'users.sso-roles.index',
    'users.external.index',
];

export const navItems: NavItem[] = navRoutes.map((route) => routeMetadata[route]);

export function getNavIcon(route: string): SvgIconComponent | undefined {
    return routeMetadata[route]?.icon;
}

export function getNavText(route: string): string | undefined {
    return routeMetadata[route]?.text;
}

// Return the current URL we are on (excluding the query string)
export function getCurrentUrl(): string {
    return window.location.origin + window.location.pathname;
}

// Return iebms event URL
export function getIebmsEventUrl($id: number): string {
    return `${window.location.origin}/iebms-events/${$id}`;
}

// Return the current URL search params
export function getCurrentParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
}

export function getBreadcrumbLabel(route: string): string {
    return routeMetadata[route].breadcrumbLabel ?? 'N/A';
}

// Helper function to detect if current URL is related to IEBMS events
export function isIebmsUrl(): boolean {
    const currentPath = window.location.pathname;
    return currentPath.indexOf('/iebms-events') !== -1 || 
           currentPath.indexOf('/iebms-reports') !== -1 || 
           currentPath.indexOf('/iebms-user-emails') !== -1 ||
           currentPath.indexOf('/iebms-report-templates') !== -1;
}